import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import HomePage from '@/pages/HomePage';
import ServicesPage from '@/pages/ServicesPage';
import AreasPage from '@/pages/AreasPage';
import CityPage from '@/pages/CityPage';
import AboutPage from '@/pages/AboutPage';
import FaqPage from '@/pages/FaqPage';
import ContactPage from '@/pages/ContactPage';
import PartnerPage from '@/pages/PartnerPage';
import ServiceDetailPage from '@/pages/ServiceDetailPage';
import PrivacyPage from '@/pages/PrivacyPage';
import ResourcesPage from '@/pages/ResourcesPage';
import ResourceArticlePage from '@/pages/ResourceArticlePage';
import { RESOURCES } from '@/lib/resources';
import NotFoundPage from '@/pages/NotFoundPage';
import { Phone } from 'lucide-react';
import { PHONE_TEL } from '@/lib/site';
import { initAnalytics, trackCallClick, trackPageView } from '@/lib/analytics';
import { Toaster } from '@/components/ui/toaster';

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    initAnalytics();
    trackPageView(`${pathname}${hash || ''}`);
    if (hash) {
      window.requestAnimationFrame(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
      });
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}


function MobileCallBar() {
  const { pathname } = useLocation();
  if (pathname === '/partner') return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-navy-deep/95 p-3 shadow-2xl backdrop-blur lg:hidden">
      <a href={PHONE_TEL} onClick={() => trackCallClick(`mobile-sticky:${pathname}`)} className="mx-auto flex max-w-md items-center justify-center gap-2 rounded-full bg-water px-6 py-3.5 text-sm font-extrabold text-navy-deep shadow-lg shadow-water/30">
        <Phone className="h-4 w-4" /> Call for Water Damage Help
      </a>
    </div>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background pb-20 lg:pb-0">
      <BrowserRouter>
        <ScrollToTop />
        <Header />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/emergency-water-extraction" element={<ServiceDetailPage slug="emergency-water-extraction" />} />
            <Route path="/services/flood-damage-restoration" element={<ServiceDetailPage slug="flood-damage-restoration" />} />
            <Route path="/services/burst-pipe-repair" element={<ServiceDetailPage slug="burst-pipe-repair" />} />
            <Route path="/services/structural-drying" element={<ServiceDetailPage slug="structural-drying" />} />
            <Route path="/services/mold-remediation" element={<ServiceDetailPage slug="mold-remediation" />} />
            <Route path="/services/sewage-cleanup" element={<ServiceDetailPage slug="sewage-cleanup" />} />
            <Route path="/areas" element={<AreasPage />} />
            <Route path="/coquitlam" element={<CityPage slug="coquitlam" />} />
            <Route path="/port-coquitlam" element={<CityPage slug="port-coquitlam" />} />
            <Route path="/port-moody" element={<CityPage slug="port-moody" />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/partner" element={<PartnerPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/what-to-do-after-basement-flooding-coquitlam-bc" element={<Navigate to="/resources/what-to-do-after-basement-flooding" replace />} />
            <Route path="/resources/basement-flooding-tri-cities" element={<Navigate to="/resources/what-to-do-after-basement-flooding" replace />} />
            {RESOURCES.map((article) => (
              <Route key={article.slug} path={`/resources/${article.slug}`} element={<ResourceArticlePage slug={article.slug} />} />
            ))}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer />
        <MobileCallBar />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
